import ExpandableDesc from '@cohort/components-xps/components/ExpandableDesc';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PerkCardSingleInstance from '@cohort/wallet/components/card/PerkCardSingleInstance';
import {MobileNavTabs} from '@cohort/wallet/components/MobileNavTabs';
import {MobileSection} from '@cohort/wallet/components/MobileSection';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import SpacePage from '@cohort/wallet/layouts/space/SpacePage';
import type {SectionName} from '@cohort/wallet/lib/Utils';
import AboutDigitalAssetPanel from '@cohort/wallet/pages/space/AboutDigitalAssetPanel';
import type {OwnershipWDto} from '@cohort/wallet-schemas/ownership';
import React, {Fragment, useCallback, useRef, useState} from 'react';

interface DigitalAssetDetailPageMobileProps {
  ownership: OwnershipWDto;
}

const DigitalAssetDetailPageMobile: React.FC<DigitalAssetDetailPageMobileProps> = ({ownership}) => {
  const {space} = useSpaceContext();
  const {hasDarkBg, backgroundColor} = useThemeContext();
  const navTabsRef = useRef<HTMLDivElement | null>(null);

  const [visibleSections, setVisibleSections] = useState<
    Partial<Record<SectionName, boolean | undefined>>
  >({});

  const handleVisibilityChange = useCallback(
    (sectionName: SectionName, isVisible: boolean): void => {
      setVisibleSections(prev => ({...prev, [sectionName]: isVisible}));
    },
    []
  );

  if (space === undefined) {
    return <Fragment />;
  }

  const sectionsNames: SectionName[] = [
    'description',
    ...(ownership.digitalAsset.perkAccesses.length > 0 ? ['perks' as SectionName] : []),
  ];

  const visibleSection = (): SectionName | undefined => {
    for (const sectionName of sectionsNames) {
      if (visibleSections[sectionName] === true) {
        return sectionName;
      }
    }
    return undefined;
  };

  return (
    <SpacePage
      title={`${ownership.digitalAsset.collection.title} #${ownership.digitalAsset.tokenId}`}
      tracking={{
        namespace: 'digitalAssets.detail',
        metadata: {
          ownershipId: ownership.id,
          digitalAssetId: ownership.digitalAsset.id,
        },
      }}
    >
      <div className="-mx-4">
        <AboutDigitalAssetPanel ownership={ownership} />

        <div ref={navTabsRef} className="sticky left-0 top-[-1rem] z-50 mt-2">
          <MobileNavTabs
            visibleSection={visibleSection()}
            sectionsNames={sectionsNames}
            tracking={{
              namespace: 'digitalAssets.detail.tabs',
            }}
          />
        </div>
        <div className={classNames(sectionsNames.length > 0 && 'mt-5')}>
          <MobileSection name="description" onVisibilityChange={handleVisibilityChange}>
            <ExpandableDesc
              hasDarkBg={hasDarkBg}
              backgroundColor={backgroundColor}
              description={ownership.digitalAsset.collection.description}
            />
          </MobileSection>

          <MobileSection name="perks" onVisibilityChange={handleVisibilityChange}>
            <div className="flex flex-col gap-4">
              {ownership.digitalAsset.perkAccesses.map(perkAccess => (
                <PerkCardSingleInstance
                  key={perkAccess.id}
                  ownership={ownership}
                  perkAccess={perkAccess}
                />
              ))}
            </div>
          </MobileSection>
        </div>
      </div>
    </SpacePage>
  );
};

export default DigitalAssetDetailPageMobile;
